import type { ButtonHTMLAttributes, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

type Kind = 'default' | 'primary' | 'ghost' | 'danger';
type Size = 'md' | 'sm';

interface Props extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  kind?: Kind;
  size?: Size;
  icon?: LucideIcon;
  children?: ReactNode;
}

const SIZE_CLS: Record<Size, string> = {
  md: 'h-[34px] px-3.5 text-[13px] rounded-[10px]',
  sm: 'h-7 px-2.5 text-[12px] rounded-[7px]',
};

const KIND_STYLE: Record<Kind, string> = {
  default:
    'bg-surface text-text border border-border-strong hover:bg-surface-2',
  primary:
    'bg-accent text-accent-text border border-accent hover:bg-accent-strong hover:border-accent-strong',
  ghost: 'text-text-2 hover:bg-surface-2 hover:text-text border border-transparent',
  danger:
    'bg-surface text-[var(--err)] border border-[color-mix(in_oklab,var(--err)_35%,var(--border))] hover:bg-surface-2',
};

export function Btn({
  kind = 'default',
  size = 'md',
  icon: Icon,
  children,
  className,
  ...rest
}: Props) {
  return (
    <button
      {...rest}
      className={[
        'inline-flex items-center gap-1.5 font-medium whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-default',
        SIZE_CLS[size],
        KIND_STYLE[kind],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {Icon && <Icon size={size === 'sm' ? 13 : 15} strokeWidth={1.7} />}
      {children}
    </button>
  );
}
